import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { BookOpen, HandCoins, CreditCard, ArrowLeftRight, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { PhotoUploader } from "@/components/PhotoUploader";
import { InstallBanner } from "@/components/InstallApp";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/lib/api";
import { LOGO_URL, fmtDate, rp } from "@/lib/format";
import { AccountNo } from "@/components/AccountNo";

const MENU = [
  { label: "Buku Tabungan", icon: BookOpen, to: "/app/tabungan", id: "savings", tone: "from-blue-600 to-sky-400" },
  { label: "Pinjaman", icon: HandCoins, to: "/app/pinjaman", id: "loans", tone: "from-orange-500 to-amber-400" },
  { label: "Pembayaran", icon: CreditCard, to: "/app/transaksi/bayar", id: "payments", tone: "from-rose-600 to-red-400" },
  { label: "Transfer Online", icon: ArrowLeftRight, to: "/app/transaksi/transfer", id: "transfer", tone: "from-violet-600 to-fuchsia-400" },
];

export const MemberCard = ({ user }) => (
  <div data-testid="digital-member-card" className="card-wave rounded-3xl border border-slate-200 shadow-xl overflow-hidden fade-up">
    <div className="px-5 pt-4 flex items-center gap-2 justify-end">
      <img src={LOGO_URL} alt="" className="h-12 w-12 object-contain bg-white keep-white rounded-full" />
      <div className="leading-none">
        <div className="font-display font-extrabold text-blue-800 text-xs">Credit Union</div>
        <div className="font-display font-extrabold text-base"><span className="text-red-600">TUAH</span> <span className="text-blue-800">TALINO</span></div>
      </div>
    </div>
    <div className="text-center font-bold text-slate-900 tracking-wide text-sm mt-2">KARTU ANGGOTA DIGITAL</div>
    <div className="flex items-start gap-4 px-5 pt-4 pb-4">
      <PhotoUploader className="h-20 w-16 rounded-xl border-2 border-white shadow text-2xl" testid="member-card-photo" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-slate-900 text-lg leading-snug break-words" data-testid="member-card-name">{user.name}</div>
        <div className="flex items-end justify-between gap-3 mt-2">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">No. Anggota</div>
            <div className="font-display font-bold text-slate-900" data-testid="member-card-number">{user.member_no}</div>
            {user.account_no && (
              <>
                <div className="text-xs text-slate-500 mt-1">No. Rekening</div>
                <AccountNo value={user.account_no} className="text-slate-900 text-sm" testid="member-card-account" />
              </>
            )}
            <div className="text-[11px] text-slate-600 mt-1">Bergabung {fmtDate(user.joined_at)}</div>
            <span data-testid="member-card-status" className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${user.status === "aktif" ? "bg-emerald-600" : "bg-slate-500"}`}>
              {user.status === "aktif" ? "Aktif" : "Nonaktif"}
            </span>
          </div>
          <div className="bg-white keep-white p-1.5 rounded-lg shadow-sm shrink-0" data-testid="member-card-qr">
            <QRCodeSVG value={`CUTUAHTALINO:${user.member_no}`} size={64} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data } = useApi("/me/summary");
  const [show, setShow] = useState(false);

  return (
    <div className="px-5 space-y-5">
      <div className="flex items-center gap-4 fade-up">
        <PhotoUploader className="h-14 w-14 rounded-full border-2 border-white shadow" testid="home-avatar-photo" />
        <div>
          <div className="text-sm text-slate-600">Selamat Datang,</div>
          <div className="text-xl font-bold text-slate-900" data-testid="welcome-name">{user.name}</div>
          <div className="text-xs text-slate-500">Anggota No. {user.member_no}</div>
        </div>
      </div>

      <MemberCard user={user} />

      <InstallBanner />

      <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-sky-400 text-white rounded-2xl p-4 shadow-lg shadow-blue-500/30 fade-up" style={{ animationDelay: "80ms" }}>
        <span className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/15" />
        <div className="relative flex items-center justify-between">
          <div>
            <div className="text-xs text-blue-50">Total Saldo Tabungan</div>
            <div className="text-2xl font-bold font-display" data-testid="home-total-balance">{show ? rp(data?.balances.total) : "Rp •••••••"}</div>
          </div>
          <button onClick={() => setShow(!show)} data-testid="home-toggle-balance" className="p-2 rounded-full bg-white/15 hover:bg-white/25 transition-colors">
            {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        <div className="relative grid grid-cols-3 gap-2 mt-3">
          {[["pokok", "Pokok", "bg-emerald-400/30"], ["wajib", "Wajib", "bg-amber-300/30"], ["sukarela", "Sukarela", "bg-fuchsia-300/30"]].map(([k, l, c]) => (
            <div key={k} className={`${c} rounded-xl px-2.5 py-2`}>
              <div className="text-[10px] text-white/85">{l}</div>
              <div className="text-xs font-bold truncate" data-testid={`home-balance-${k}`}>{show ? rp(data?.balances[k]) : "•••••"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {MENU.map(({ label, icon: Icon, to, id, tone }, i) => (
          <button key={id} onClick={() => navigate(to)} data-testid={`home-menu-${id}`}
            className="bg-white border border-slate-200 rounded-2xl py-5 flex flex-col items-center gap-2.5 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-[box-shadow,transform] fade-up"
            style={{ animationDelay: `${120 + i * 60}ms` }}>
            <span className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${tone} text-white flex items-center justify-center shadow-md`}><Icon className="h-6 w-6" strokeWidth={1.9} /></span>
            <span className="text-sm font-semibold text-slate-900">{label}</span>
          </button>
        ))}
      </div>

      {data?.active_loan && (
        <button onClick={() => navigate("/app/pinjaman")} data-testid="home-active-loan" className="w-full text-left bg-gradient-to-r from-orange-500 to-amber-400 text-white rounded-2xl p-4 shadow-lg shadow-orange-500/30">
          <div className="text-xs text-white/90 font-semibold">Angsuran berikutnya</div>
          <div className="flex justify-between items-end mt-1">
            <div className="font-bold text-lg">{rp(data.active_loan.monthly_installment)}</div>
            <div className="text-xs text-white/90">Jatuh tempo {fmtDate(data.active_loan.next_due)}</div>
          </div>
        </button>
      )}
    </div>
  );
}
